import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "../api/http";
import SeoScoreCard, { SeoScoreHero } from "../components/SeoScoreCard";
import AuditFeedbackWidget from "../components/AuditFeedbackWidget";
import { useAuth } from "../context/AuthContext";

function updateMetaTag(attribute, value, nameKey = "name") {
  let tag = document.querySelector(`meta[${nameKey}="${attribute}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(nameKey, attribute);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
  return tag;
}

export default function ReportView() {
  const { shareToken } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    let mounted = true;
    const fetchReport = async () => {
      try {
        const { data } = await apiGet(`/api/seo/reports/${shareToken}`);
        if (!mounted) return;
        setReport({
          ...data,
          images: data.images || {},
        });
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.error ||
          "Unable to load this shared report. It may have expired or been removed."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      mounted = false;
    };
  }, [shareToken]);

  useEffect(() => {
    const originalTitle = document.title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    const ogDescriptionTag = document.querySelector('meta[property="og:description"]');

    const originalDescription = descriptionTag?.getAttribute("content") || "";
    const originalOgTitle = ogTitleTag?.getAttribute("content") || "";
    const originalOgDescription = ogDescriptionTag?.getAttribute("content") || "";

    if (report) {
      document.title = `SEO Audit Report | ${report.url}`;
      updateMetaTag("description", `SEO audit report for ${report.url}. See score, issues, and priority fixes. Audit your own site at ${window.location.origin}.`);
      updateMetaTag("og:title", `SEO Audit Report — ${report.url}`, "property");
      updateMetaTag("og:description", `SEO audit report for ${report.url}. See score, issues, and priority fixes.`, "property");
    } else {
      document.title = "SEO Audit Report";
    }

    return () => {
      document.title = originalTitle;
      if (descriptionTag) descriptionTag.setAttribute("content", originalDescription);
      if (ogTitleTag) ogTitleTag.setAttribute("content", originalOgTitle);
      if (ogDescriptionTag) ogDescriptionTag.setAttribute("content", originalOgDescription);
    };
  }, [report]);

  return (
    <div className="page">
      <h1 className="page-title">Shared SEO Audit Report</h1>
      <p className="dashboard-lead" style={{ marginTop: -8 }}>
        This is a read-only snapshot of the audit. Anyone with the link can view it, but the site remains protected.
      </p>

      {loading ? (
        <div className="loading">Loading report…</div>
      ) : error ? (
        <div className="card">
          <div className="progress-error">{error}</div>
          <div style={{ marginTop: 16 }}>
            <Link to="/" className="btn btn-primary">
              Audit your own site
            </Link>
          </div>
        </div>
      ) : (
        <>
          <SeoScoreHero report={report} />
          <SeoScoreCard report={report} showHero={false} />

          <AuditFeedbackWidget auditId={report.id} url={report.url} />

          {!isAuthenticated && (
            <div className="card" style={{ marginTop: 24, textAlign: "center" }}>
              <h2>Run your own SEO audit</h2>
              <p>Audit any website in seconds and get a shareable report with score, issue priorities, and next-step fixes.</p>
              <Link to="/" className="btn btn-primary">
                Audit your own site
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
